import { createBigDecimal, toStringBigDecimal } from '@0xtorch/big-decimal'
/**
 * 会計 transaction を DB insert 用の row 群へ変換する。
 * transaction 本体と transfer を同じ規則で直列化し、bulk insert / rewrite の両方から再利用する。
 */
import type { Transaction } from '@pkg/accounting'
import type { SQLiteInsertValue } from 'drizzle-orm/sqlite-core'
import type {
  accountingTransactionTable,
  accountingTransactionTransferTable,
} from '../schema'

type TransactionTransferInsertSql = SQLiteInsertValue<
  typeof accountingTransactionTransferTable
>

type TransactionsToInsertSqlsReturnTypes = {
  transactionSqls: readonly SQLiteInsertValue<
    typeof accountingTransactionTable
  >[]
  transferSqls: readonly TransactionTransferInsertSql[]
  transactionIds?: readonly string[]
}

type TransactionsToTransferSqlsReturnTypes = {
  transferSqls: readonly TransactionTransferInsertSql[]
  transactionIds: readonly string[]
}

type AppendTransferSqlsParameters = {
  readonly transferSqls: TransactionTransferInsertSql[]
  readonly transaction: Transaction
  readonly txId: string
}

/**
 * 1 transaction に紐づく in/out transfer を insert row 配列へ追加する。
 * @param params 追加先配列と変換対象 transaction を受け取る。
 */
const appendTransferSqls = ({
  transferSqls,
  transaction,
  txId,
}: AppendTransferSqlsParameters): void => {
  let order = -1

  for (const transfer of transaction.ins) {
    order += 1
    transferSqls.push({
      transactionId: txId,
      order,
      isIn: true,
      cryptoId:
        transfer.asset.type === 'CryptoCurrency'
          ? transfer.asset.id
          : undefined,
      fiatId:
        transfer.asset.type === 'FiatCurrency' ? transfer.asset.id : undefined,
      nftId: transfer.asset.type === 'Nft' ? transfer.asset.id : undefined,
      amount: toStringBigDecimal(transfer.amount),
      price: toStringBigDecimal(transfer.price),
      zeroCompleted: transfer.zeroCompleted,
      cost: toStringBigDecimal(transfer.cost),
      balance: toStringBigDecimal(transfer.balance),
      borrowing: toStringBigDecimal(transfer.borrowing),
      totalPl: toStringBigDecimal(transfer.totalPl),
      tradePl: toStringBigDecimal(transfer.tradePl),
      separateTradePl: toStringBigDecimal(
        transfer.separateTradePl ?? createBigDecimal(0n),
      ),
      nftTradePl: toStringBigDecimal(
        transfer.nftTradePl ?? createBigDecimal(0n),
      ),
      nftCapitalPl: toStringBigDecimal(
        transfer.nftCapitalPl ?? createBigDecimal(0n),
      ),
      incomePl: toStringBigDecimal(transfer.incomePl),
      separateRealizedPl: toStringBigDecimal(
        transfer.separateRealizedPl ?? createBigDecimal(0n),
      ),
      realizedPl: toStringBigDecimal(
        transfer.realizedPl ?? createBigDecimal(0n),
      ),
      airdropPl: toStringBigDecimal(transfer.airdropPl ?? createBigDecimal(0n)),
      feePl: toStringBigDecimal(transfer.feePl),
    })
  }

  for (const transfer of transaction.outs) {
    order += 1
    transferSqls.push({
      transactionId: txId,
      order,
      isIn: false,
      cryptoId:
        transfer.asset.type === 'CryptoCurrency'
          ? transfer.asset.id
          : undefined,
      fiatId:
        transfer.asset.type === 'FiatCurrency' ? transfer.asset.id : undefined,
      nftId: transfer.asset.type === 'Nft' ? transfer.asset.id : undefined,
      amount: toStringBigDecimal(transfer.amount),
      price: toStringBigDecimal(transfer.price),
      zeroCompleted: transfer.zeroCompleted,
      cost: toStringBigDecimal(transfer.cost),
      balance: toStringBigDecimal(transfer.balance),
      borrowing: toStringBigDecimal(transfer.borrowing),
      totalPl: toStringBigDecimal(transfer.totalPl),
      tradePl: toStringBigDecimal(transfer.tradePl),
      separateTradePl: toStringBigDecimal(
        transfer.separateTradePl ?? createBigDecimal(0n),
      ),
      nftTradePl: toStringBigDecimal(
        transfer.nftTradePl ?? createBigDecimal(0n),
      ),
      nftCapitalPl: toStringBigDecimal(
        transfer.nftCapitalPl ?? createBigDecimal(0n),
      ),
      incomePl: toStringBigDecimal(transfer.incomePl),
      separateRealizedPl: toStringBigDecimal(
        transfer.separateRealizedPl ?? createBigDecimal(0n),
      ),
      realizedPl: toStringBigDecimal(
        transfer.realizedPl ?? createBigDecimal(0n),
      ),
      airdropPl: toStringBigDecimal(transfer.airdropPl ?? createBigDecimal(0n)),
      feePl: toStringBigDecimal(transfer.feePl),
    })
  }
}

/**
 * transaction 群から transfer insert row と transaction id 一覧だけを生成する。
 * @param transactions 変換対象の transaction 一覧。
 * @returns bulk rewrite に使う transfer row 群と transaction id 一覧。
 */
export const transactionsToTransferSqls = (
  transactions: readonly Transaction[],
): TransactionsToTransferSqlsReturnTypes => {
  const transactionIds = new Set<string>()
  const transferSqls: TransactionTransferInsertSql[] = []

  for (const transaction of transactions) {
    const txId = `${transaction.actionId}_${transaction.order}`
    transactionIds.add(txId)
    appendTransferSqls({ transferSqls, transaction, txId })
  }

  return {
    transferSqls,
    transactionIds: [...transactionIds],
  }
}

/**
 * transaction 群を transaction table / transfer table 両方の insert row へ変換する。
 * @param transactions 変換対象の transaction 一覧。
 * @param accountingPeriodId 保存先会計期間 ID。
 * @returns bulk insert に必要な row 群と transaction id 一覧。
 */
export const transactionsToInsertSqls = (
  transactions: readonly Transaction[],
  accountingPeriodId: number,
  params?: {
    readonly collectTransactionIds?: boolean
  },
): TransactionsToInsertSqlsReturnTypes => {
  const collectTransactionIds = params?.collectTransactionIds ?? true
  const transactionIds = collectTransactionIds ? new Set<string>() : undefined
  const transactionSqls: SQLiteInsertValue<
    typeof accountingTransactionTable
  >[] = []
  const transferSqls: TransactionTransferInsertSql[] = []

  const updatedAt = new Date()

  for (const transaction of transactions) {
    const txId = `${transaction.actionId}_${transaction.order}`
    transactionIds?.add(txId)
    transactionSqls.push({
      id: txId,
      accountingPeriodId,
      actionId: transaction.actionId,
      order: transaction.order,
      category: transaction.category,
      subType: transaction.subType,
      subCategory: transaction.subCategory,
      targetId: transaction.target?.id,
      timestamp: new Date(transaction.timestamp),
      comment: transaction.comment,
      updatedAt,
    })
    appendTransferSqls({ transferSqls, transaction, txId })
  }

  return {
    transactionSqls,
    transferSqls,
    transactionIds:
      transactionIds === undefined ? undefined : [...transactionIds],
  }
}
